import axios, {AxiosResponse} from 'axios';
import {
    AuditTrailDialogResponse,
    AvailableToDownloadReportResponse,
    CaseAESummaryResponse,
    CaseAutoNarrativeRetrieveResponse,
    CaseDGSummaryResponse,
    CaseDHSummaryResponse,
    CaseDuplicationCheckResponse,
    CaseEmailLogDetailRetrieveResponse,
    CaseEmailLogsRetrieveResponse,
    CaseInfoProductData,
    CaseInfoStudyRetrieveResponse,
    CaseItemCodesRetrieveResponse,
    CaseKcdRetrieveResponse,
    CaseKeys,
    CaseLBSummaryResponse,
    CaseMeddraRetrieveResponse,
    CasePagesRetrieveResponse,
    CaseProductRetrieveResponse,
    CaseRepeatPageRow,
    CaseRepeatPageStatusData,
    CaseReportEmailsRetrieveResponse,
    CaseRPSummaryResponse,
    CasesRetrieveResponse,
    CaseStatusData,
    CaseStatusResponse,
    CaseTopMenuAuditTrailDialogResponse,
    CaseVersionRetrieveResponse,
    CaseWarningssRetrieveResponse,
    CaseWhoartRetrieveResponse,
    CodesRetrievePaginatedResponse,
    ColumnRetrieveResponse,
    ExcelExportResponse,
    KimsRetrieveResponse,
    MfdsProductRetrieveResponse,
    MfdsSubstanceRetrieveResponse,
    NullDataResponse,
    ReceiverRetrieveResponse,
    TranslationResponse,
    WhoddCasRetrieveResponse,
    CaseNsdeDictionaryDialogResponse,
    CaseUniiDictionaryDialogResponse,
    CaseDeviceProblemCodeDictionaryDialogResponse,
    CaseDeviceComponentCodeDictionaryDialogResponse,
    UserAuthResponse,
    WorkflowPrivilegeResponse,
    BaseResponse,
    WorkflowBatchInfoRequest
} from '@/model/CommonModel';
import ApiUtils from '@/utils/ApiUtils';

export default class CaseApi {
    public static retrieveCases(params: any = {}): Promise<CasesRetrieveResponse> {
        return axios
            .get(`/1.0/cases`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    private static readonly caseItemCodeMap: Map<string, CaseItemCodesRetrieveResponse> = new Map();
    
    public static retrieveCaseItemCodes(itemId: string, params: any = {}): Promise<CaseItemCodesRetrieveResponse> {
        const key = `${itemId}-${params.language}`;
        const cachedResponse: CaseItemCodesRetrieveResponse | undefined = CaseApi.caseItemCodeMap.get(key);
        
        if (cachedResponse) {
            // console.log(`${key} of response was cached`); // for dubbing
            return Promise.resolve(cachedResponse);
        } else {
            return axios
                .get(`/1.1/items/${itemId}/code-list`, {params})
                .then((res: AxiosResponse) => res.data)
                .then((res: CaseItemCodesRetrieveResponse) => {
                    if (key.indexOf('MEDDRA_VER') > -1 || key.indexOf('MAT_AE_ROW_NO') > -1
                        || key.indexOf('DM_CON_THERAPHY') > -1 || key.indexOf('MH_FAM_HIS') > -1
                        || key.indexOf('AE_SDTH') > -1 || key.indexOf('AE_SLIFE') > -1
                        || key.indexOf('AE_SHOSP') > -1 || key.indexOf('AE_SDISAB') > -1
                        || key.indexOf('AE_SCONG') > -1 || key.indexOf('AE_SMIE') > -1) {
                        return res;
                    }
                    
                    CaseApi.caseItemCodeMap.set(key, res);
                    return res;
                });
        }
    }
    
    public static retrievePaginatedCaseItemCodes(itemId: string, params: any = {}): Promise<CodesRetrievePaginatedResponse> {
        return axios.get(`/1.1/items/${itemId}/code-list?chunk`, {params})
                    .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseItemNullFlavors(itemId: string): Promise<CaseItemCodesRetrieveResponse> {
        return axios
            .get(`/1.1/items/${itemId}/null-flavor-list`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseNo(caseKey: string): Promise<any> {
        return axios
            .get(`/1.0/cases/${caseKey}/case-id`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseVersions(caseKey: string): Promise<CaseVersionRetrieveResponse> {
        return axios
            .get(`/1.0/cases/${caseKey}/versions`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveTranslation(sponsorKey: string, valueToTranslate: string): Promise<TranslationResponse> {
        return axios
            .post(`/1.1/sponsors/${sponsorKey}/translate`, {valueToTranslate})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static downloadCaseFile(caseKey: string, pageId: string, rowNo: number, itemId: string) {
        return axios
            .get(`/1.0/cases/${caseKey}/pages/${pageId}/rows/${rowNo}`, {params: {itemId}, responseType: 'blob'})
            .then((res: any) => ApiUtils.isFailToFileDownloadResponse(res)
                ? ApiUtils.handleFileDownloadFailResponse(res)
                : ApiUtils.handleFileDownloadSuccessResponse(res));
    }
    
    public static retrieveCaseCi(caseKey: string): Promise<any> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/CI`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static saveCaseCi(caseKey: string, param: any): Promise<any> {
        return axios
            .post(`/1.1/cases/${caseKey}/pages/CI`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseRPSummary(caseKey: string, params: any): Promise<CaseRPSummaryResponse> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/RP/summary`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseSummary(caseKey: string, pageId: string, params: any): Promise<any> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/${pageId}/summary`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseRowDetail(caseKey: string, pageId: string, rowNo: string): Promise<any> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/${pageId}/${rowNo}`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static saveCaseRowDetail(caseKey: string, pageId: string, rowNo: string, param: any): Promise<any> {
        return axios
            .post(`/1.1/cases/${caseKey}/pages/${pageId}/${rowNo}`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseRPDetail(caseKey: string, rowNo: string): Promise<any> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/RP/${rowNo}`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static saveCaseRP(caseKey: string, rowNo: string, param: any): Promise<any> {
        return axios
            .post(`/1.1/cases/${caseKey}/pages/RP/${rowNo}`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseDemo(caseKey: string): Promise<any> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/DM`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseAESummary(caseKey: string, params: any): Promise<CaseAESummaryResponse> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/AE/summary`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseAEDetail(caseKey: string, rowNo: string): Promise<any> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/AE/${rowNo}`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static saveCaseAE(caseKey: string, rowNo: string, param: any): Promise<any> {
        return axios
            .post(`/1.1/cases/${caseKey}/pages/AE/${rowNo}`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseDHSummary(caseKey: string, params: any): Promise<CaseDHSummaryResponse> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/DH/summary`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseDHDetail(caseKey: string, rowNo: string): Promise<any> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/DH/${rowNo}`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static saveCaseDH(caseKey: string, rowNo: string, param: any): Promise<any> {
        return axios
            .post(`/1.1/cases/${caseKey}/pages/DH/${rowNo}`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseDGDetail(caseKey: string, rowNo: string): Promise<any> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/DG/${rowNo}`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseLBSummary(caseKey: string, params: any): Promise<CaseLBSummaryResponse> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/LB/summary`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseLBDetail(caseKey: string, rowNo: string): Promise<any> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/LB/${rowNo}`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static saveCaseLB(caseKey: string, rowNo: string, param: any): Promise<any> {
        return axios
            .post(`/1.1/cases/${caseKey}/pages/LB/${rowNo}`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseDGSummary(caseKey: string, params: any): Promise<CaseDGSummaryResponse> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/DG/summary`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseDGProductInfo(sponsorKey: string, prdKey: number): Promise<CaseInfoProductData> {
        return axios
            .get(`/1.1/sponsors/${sponsorKey}/products/${prdKey}?CaseDrug`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseSIDetail(caseKey: string): Promise<any> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/SI`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseSiStudyInfo(sponsorKey: string, studyKey: string): Promise<CaseInfoStudyRetrieveResponse> {
        return axios
            .get(`/1.1/sponsors/${sponsorKey}/studies/${studyKey}?CaseStudy`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseAT(caseKey: string, params: any): Promise<any> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/AT`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static saveCaseSi(caseKey: string, param: any): Promise<any> {
        return axios
            .post(`/1.1/cases/${caseKey}/pages/SI`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveMeddraCodes(version: string, params: any): Promise<CaseMeddraRetrieveResponse> {
        return axios
            .get(`/1.1/dictionaries/MEDDRA/versions/${version}`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveKcdCodes(version: string, params: any): Promise<CaseKcdRetrieveResponse> {
        return axios
            .get(`/1.1/dictionaries/KCD/versions/${version}`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveWhoartCodes(version: string, params: any): Promise<CaseWhoartRetrieveResponse> {
        return axios
            .get(`/1.1/dictionaries/WHOART/versions/${version}`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveKimsCodes(params: any): Promise<KimsRetrieveResponse> {
        return axios
            .get(`/1.1/dictionaries/KIMS/versions/any`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveNsdeCodes(params: any = {}): Promise<CaseNsdeDictionaryDialogResponse> {
        return axios
          .get(`/1.1/dictionaries/NSDE/versions/any`, {params})
          .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveUniiCodes(params: any = {}): Promise<CaseUniiDictionaryDialogResponse> {
        return axios
          .get(`/1.1/dictionaries/UNII/versions/any`, {params})
          .then((res: AxiosResponse) => res.data);
    }

    public static retrieveDeviceProblemCodes(params: any = {}): Promise<CaseDeviceProblemCodeDictionaryDialogResponse> {
        return axios
          .get(`/1.1/dictionaries/DPC/versions/any`, {params})
          .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveDeviceComponentCodes(params: any = {}): Promise<CaseDeviceComponentCodeDictionaryDialogResponse> {
        return axios
          .get(`/1.1/dictionaries/DCC/versions/any`, {params})
          .then((res: AxiosResponse) => res.data);
    }

    public static retrieveMfdsProductCodes(version: string, params: any): Promise<MfdsProductRetrieveResponse> {
        return axios
            .get(`/1.1/dictionaries/MFDS_PRODUCT/versions/${version}`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveMfdsSubstanceCodes(version: string, params: any): Promise<MfdsSubstanceRetrieveResponse> {
        return axios
            .get(`/1.1/dictionaries/MFDS_INGREDIENT/versions/${version}`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveWhoddCasCodes(version: string, params: any): Promise<WhoddCasRetrieveResponse> {
        return axios
            .get(`/1.1/dictionaries/WHODD_CAS/versions/${version}`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseProducts(caseKey: string, params: any): Promise<CaseProductRetrieveResponse> {
        return axios
            .get(`/1.1/cases/common/${caseKey}/products`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCasePageDetail(caseKey: string, pageId: string): Promise<any> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/${pageId}`)
            .then((res: AxiosResponse) => res.data);
    }

    public static saveCasePage(caseKey: string, pageId: string, param: any): Promise<any> {
        return axios
            .post(`/1.1/cases/${caseKey}/pages/${pageId}`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseDuplication(param: any): Promise<CaseDuplicationCheckResponse> {
        return axios
            .post('/1.1/cases/common/duplicate-list', param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static addCase(param: any): Promise<CaseDuplicationCheckResponse> {
        return axios
            .post('/1.1/cases/common/case-add', param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseWarningColumns(): Promise<ColumnRetrieveResponse> {
        return axios
            .get(`/1.0/menus/MU-CS-WARNING/columns`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseWarnings(params: any = {}): Promise<CaseWarningssRetrieveResponse> {
        return axios
            .get(`/1.0/cases/warning`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseEmailLogColumns(): Promise<ColumnRetrieveResponse> {
        return axios
            .get(`/1.0/menus/MU-CS-LOG/columns`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseEmailLogs(params: any = {}): Promise<CaseEmailLogsRetrieveResponse> {
        return axios
            .get(`/1.0/email-logs`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseEmailLogDetail(emailKey: number | string): Promise<CaseEmailLogDetailRetrieveResponse> {
        return axios
            .get(`/1.0/email-logs/${emailKey}`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static downloadEmailLogFile(sponsorKey: number, fileKey: number): Promise<any> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/files/${fileKey}`, {responseType: 'blob'})
            .then((res: any) => ApiUtils.isFailToFileDownloadResponse(res)
                ? ApiUtils.handleFileDownloadFailResponse(res)
                : ApiUtils.handleFileDownloadSuccessResponse(res));
    }
    
    public static retrieveCasePages(caseKey: string): Promise<CasePagesRetrieveResponse> {
        return axios
            .get(`/1.0/cases/${caseKey}/pages`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static createWarnings(caseKey: string): Promise<any> {
        return axios
            .get(`/1.0/cases/${caseKey}/pages`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveAvailableReports(caseKey: string): Promise<AvailableToDownloadReportResponse> {
        return axios
            .get(`/1.0/cases/${caseKey}/reports`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static checkReport(caseKey: string, reportId: string, params: any = {}): Promise<any> {
        return axios
            .get(`/1.0/cases/${caseKey}/reports-check/${reportId}`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static downloadReport(caseKey: string, reportId: string, params: any = {}): Promise<any> {
        return axios
            .get(`/1.0/cases/${caseKey}/reports/${reportId}`, {params, responseType: 'blob'})
            .then((res: any) => ApiUtils.isFailToFileDownloadResponse(res)
                ? ApiUtils.handleFileDownloadFailResponse(res)
                : ApiUtils.handleFileDownloadSuccessResponse(res));
    }
    
    public static retrieveReceivers(sponsorKey: number, caseKey: number, senderKey: number) {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/cases/receivers?caseKey=${caseKey}&senderKey=${senderKey}`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseReportEmails(caseKey: string): Promise<CaseReportEmailsRetrieveResponse> {
        return axios
            .get(`/1.0/cases/${caseKey}/reports/email-list`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseReportEmailAndDownloadReceivers(sponsorKey: string | number,
                                                              params: any = {}): Promise<ReceiverRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/cases/receivers`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static sendCaseReportEmail(caseKey: string | number, param: any, params: any = {}): Promise<any> {
        return axios
            .put(`/1.0/cases/${caseKey}/email`, param, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    /**
     * CASE > Top Menu에서의 Audit Trail
     * @param {number} caseKey
     * @returns {Promise<CaseTopMenuAuditTrailDialogResponse>}
     */
    public static getCaseAuditTrailOnTopMenu(caseKey: string): Promise<CaseTopMenuAuditTrailDialogResponse> {
        return axios
            .get(`/1.0/cases/${caseKey}/audit-trail`)
            .then((res: AxiosResponse) => res.data);
        
    }
    
    public static review(caseKey: string, reason: string = ''): Promise<any> {
        return axios
            .put(`/1.0/cases/${caseKey}/status`, {
                statusId: 'REVIEW',
                status: 'Y',
                reason
            })
            .then((res: AxiosResponse) => res.data);
    }
    
    public static unReview(caseKey: string, reason: string = ''): Promise<any> {
        return axios
            .put(`/1.0/cases/${caseKey}/status`, {
                statusId: 'REVIEW',
                status: 'N',
                reason
            })
            .then((res: AxiosResponse) => res.data);
    }
    
    public static lock(caseKey: string, reason: string = ''): Promise<any> {
        return axios
            .put(`/1.0/cases/${caseKey}/status`, {
                statusId: 'LOCK',
                status: 'Y',
                reason
            })
            .then((res: AxiosResponse) => res.data);
    }
    
    public static unLock(caseKey: string, reason: string = ''): Promise<any> {
        return axios
            .put(`/1.0/cases/${caseKey}/status`, {
                statusId: 'LOCK',
                status: 'N',
                reason
            })
            .then((res: AxiosResponse) => res.data);
    }
    
    public static delete(caseKey: string, reason: string): Promise<any> {
        return axios
            .put(`/1.0/cases/${caseKey}/status`, {
                statusId: 'DELETE',
                status: 'Y',
                reason
            })
            .then((res: AxiosResponse) => res.data);
    }
    
    public static restore(caseKey: string, reason: string): Promise<any> {
        return axios
            .put(`/1.0/cases/${caseKey}/status`, {
                statusId: 'DELETE',
                status: 'N',
                reason
            })
            .then((res: AxiosResponse) => res.data);
    }
    
    public static followUp(caseKey: string): Promise<any> {
        return axios
            .put(`/1.0/cases/${caseKey}/follow-up`, {
                reason: ''
            })
            .then((res: AxiosResponse) => res.data);
    }
    
    public static getAuditTrail(caseKey: number, pageId: string = '', rowNo: number,
                                rowNo2: number, rowNo3: number, itemId: string): Promise<AuditTrailDialogResponse> {
        const url
            = `/1.0/cases/${caseKey}/pages/${pageId}/item-audit-trail?rowNo=${rowNo}&rowNo2=${rowNo2}&rowNo3=${rowNo3}&itemId[]=${itemId}`;
        return axios.get(url).then((res: AxiosResponse) => res.data);
    }
    
    public static getBlindAuditTrails(caseKey: number, pageId: string = '', rowNo: number, rowNo2: number, rowNo3: number,
                                      itemId: string): Promise<AuditTrailDialogResponse> {
        const url
            = `/1.0/cases/${caseKey}/pages/${pageId}/blind-item-audit-trail?rowNo=${rowNo}&rowNo2=${rowNo2}&rowNo3=${rowNo3}&itemId[]=${itemId}`;
        return axios.get(url).then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseWFDetail(caseKey: string, params: any): Promise<any> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/WF`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseWFAuditTrail(caseKey: string, params: any): Promise<any> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/WF/audit-trail`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static saveCaseWF(caseKey: string, param: any): Promise<any> {
        return axios
            .post(`/1.1/cases/${caseKey}/pages/WF`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseAutoNarrative(sponsorKey: number, nrKey: number, caseKey: number)
        : Promise<CaseAutoNarrativeRetrieveResponse> {
        return axios
            .get(`/1.0//sponsors/${sponsorKey}/narratives/${nrKey}/auto?caseKey=${caseKey}`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseRepeatPagePrevRow(caseKey: string, pageId: string, rowNo: string): Promise<CaseRepeatPageRow> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/${pageId}/${rowNo}?move`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseRepeatPageNextRow(caseKey: string, pageId: string, rowNo: string): Promise<CaseRepeatPageRow> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/${pageId}/${rowNo}?move`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static exportCasesExcel(): Promise<ExcelExportResponse> {
        return axios
            .get(`/1.0/cases/export`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveCaseReceiverHistory(caseKey: string): Promise<any> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/RE/distribution-history`)
            .then((res: AxiosResponse) => res.data);
    }

    public static retrieveCasesStatus(statusId: string, param: CaseKeys): Promise<CaseStatusResponse> {
        return axios
            .post(`/1.0/cases/status/${statusId}`, param)
            .then((res: AxiosResponse) => res.data);
    }

    public static updateCasesStatus(param: CaseStatusData): Promise<NullDataResponse> {
        return axios
            .put(`/1.0/cases/status`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrievePrimarySourceRowsStatus(caseKey: string, param: number[]): Promise<CaseStatusResponse> {
        return axios
            .post(`/1.1/cases/${caseKey}/pages/RP/summary/check-status`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static updatePrimarySourceRowsStatus(caseKey: string, param: CaseRepeatPageStatusData): Promise<CaseStatusResponse> {
        return axios
            .put(`/1.1/cases/${caseKey}/pages/RP/summary/status`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveLiteratureRowsStatus(caseKey: string, param: number[]): Promise<CaseStatusResponse> {
        return axios
            .post(`/1.1/cases/${caseKey}/pages/LR/summary/check-status`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static updateLiteratureRowsStatus(caseKey: string, param: CaseRepeatPageStatusData): Promise<CaseStatusResponse> {
        return axios
            .put(`/1.1/cases/${caseKey}/pages/LR/summary/status`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveAdverseEventRowsStatus(caseKey: string, param: number[]): Promise<CaseStatusResponse> {
        return axios
            .post(`/1.1/cases/${caseKey}/pages/AE/summary/check-status`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static updateAdverseEventRowsStatus(caseKey: string, param: CaseRepeatPageStatusData): Promise<CaseStatusResponse> {
        return axios
            .put(`/1.1/cases/${caseKey}/pages/AE/summary/status`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveDrugRowsStatus(caseKey: string, param: number[]): Promise<CaseStatusResponse> {
        return axios
            .post(`/1.1/cases/${caseKey}/pages/DG/summary/check-status`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static updateDrugRowsStatus(caseKey: string, param: CaseRepeatPageStatusData): Promise<CaseStatusResponse> {
        return axios
            .put(`/1.1/cases/${caseKey}/pages/DG/summary/status`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveDrugHistoryRowsStatus(caseKey: string, param: number[]): Promise<CaseStatusResponse> {
        return axios
            .post(`/1.1/cases/${caseKey}/pages/DH/summary/check-status`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static updateDrugHistoryRowsStatus(caseKey: string, param: CaseRepeatPageStatusData): Promise<CaseStatusResponse> {
        return axios
            .put(`/1.1/cases/${caseKey}/pages/DH/summary/status`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveLabRowsStatus(caseKey: string, param: number[]): Promise<CaseStatusResponse> {
        return axios
            .post(`/1.1/cases/${caseKey}/pages/LB/summary/check-status`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static updateLabRowsStatus(caseKey: string, param: CaseRepeatPageStatusData): Promise<CaseStatusResponse> {
        return axios
            .put(`/1.1/cases/${caseKey}/pages/LB/summary/status`, param)
            .then((res: AxiosResponse) => res.data);
    }

    public static retrieveUsersByRoleIdAndCase(caseKey: string, roleId: number): Promise<UserAuthResponse> {
        return axios
            .get(`/1.1/cases/${caseKey}/pages/WF/roles/${roleId}/users`)
            .then((res: AxiosResponse) => res.data);
    }

    public static retrieveBatchWorkflowPrivileges(): Promise<WorkflowPrivilegeResponse> {
        return axios
          .get(`/1.0/cases/batch-workflow/privilege`)
          .then((res: AxiosResponse) => res.data);
    }
    public static retrieveBatchWorkflowValidation(caseKeys: number[]): Promise<WorkflowBatchInfoRequest> {
        return axios
          .post(`/1.0/cases/batch-workflow/validation`, {caseKeys})
          .then((res: AxiosResponse) => res.data);
    }

    public static retrieveUsersByRoleId(roleId: number): Promise<UserAuthResponse> {
        return axios
          .get(`/1.0/cases/batch-workflow/roles/${roleId}/users`)
          .then((res: AxiosResponse) => res.data);
    }

    public static updateBatchWorkflow(params: any): Promise<BaseResponse> {
        return axios
          .put(`/1.0/cases/batch-workflow`, params)
          .then((res: AxiosResponse) => res.data);
    }

}
